import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, Send, Radio, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import juniorBusto from "@/assets/junior-busto-nobg.png";

type Msg = { role: "user" | "assistant"; content: string };

// Renderiza marcação estilo WhatsApp: *negrito*, _itálico_, ~riscado~
// Também aceita **negrito** (markdown) por compatibilidade.
const renderInline = (text: string): React.ReactNode[] => {
  const normalized = text.replace(/\*\*(.+?)\*\*/g, "*$1*");
  const nodes: React.ReactNode[] = [];
  const regex = /\*(.+?)\*|_(.+?)_|~(.+?)~/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = regex.exec(normalized)) !== null) {
    if (m.index > last) nodes.push(normalized.slice(last, m.index));
    if (m[1]) nodes.push(<strong key={k++} className="font-semibold">{m[1]}</strong>);
    else if (m[2]) nodes.push(<em key={k++} className="italic">{m[2]}</em>);
    else if (m[3]) nodes.push(<span key={k++} className="line-through">{m[3]}</span>);
    last = m.index + m[0].length;
  }
  if (last < normalized.length) nodes.push(normalized.slice(last));
  return nodes;
};

const renderContent = (content: string): React.ReactNode =>
  content.split("\n").map((line, i, arr) => (
    <span key={i}>
      {renderInline(line)}
      {i < arr.length - 1 && <br />}
    </span>
  ));

const SUGESTOES = [
  "É muito caro",
  "Minha equipe já sabe vender",
  "IA não simula cliente real",
  "Não tenho tempo pra implantar",
];

const HeroObjecaoChat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [limiteAtingido, setLimiteAtingido] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Carrega conversa do servidor (por IP)
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("hero-objecao", {
          body: { action: "load" },
        });
        if (error) return;
        const d = data as any;
        if (Array.isArray(d?.messages)) setMessages(d.messages);
        if (d?.limitReached) setLimiteAtingido(true);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const enviar = async (texto: string) => {
    const objecao = texto.trim();
    if (!objecao || loading || limiteAtingido) return;
    setErro(null);
    const novoHistorico: Msg[] = [...messages, { role: "user", content: objecao }];
    setMessages(novoHistorico);
    setInput("");
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("hero-objecao", {
        body: { action: "send", objecao },
      });
      if (error) throw error;
      const d = data as any;
      const resposta = d?.resposta || "";
      if (!resposta) throw new Error("Resposta vazia");
      setMessages([...novoHistorico, { role: "assistant", content: resposta }]);
      if (d?.limitReached) setLimiteAtingido(true);
    } catch (e: any) {
      console.error(e);
      setErro("Não consegui responder agora. Tente de novo em instantes.");
    } finally {
      setLoading(false);
    }
  };

  const temConversa = messages.length > 0;

  return (
    <Card className="max-w-3xl mx-auto border-primary/30 bg-card/80 backdrop-blur shadow-glow overflow-hidden text-left">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60 bg-gradient-to-r from-primary/10 to-accent/10">
        <div className="relative w-14 h-14 rounded-full bg-primary/20 overflow-hidden shrink-0 ring-2 ring-primary/40">
          <img
            src={juniorBusto}
            alt="Treinador Vendas AI"
            className="absolute inset-0 w-full h-full object-cover object-top scale-110"
          />
          <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-card" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-foreground">Treinador Vendas AI</p>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              <Radio className="w-3 h-3" /> AO VIVO
            </span>
          </div>
          <p className="text-xs text-muted-foreground">Me diga por que você <em>não</em> compraria. Eu te mostro o outro lado.</p>
        </div>
      </div>

      <div ref={scrollRef} className="px-5 py-5 space-y-3 min-h-[180px] max-h-[320px] overflow-y-auto">
        {!temConversa && (
          <div className="text-sm text-muted-foreground">
            Digite sua objeção real (ex.: <span className="text-foreground">"é caro demais"</span>) e veja a IA responder na hora.
            <div className="mt-3 flex flex-wrap gap-2">
              {SUGESTOES.map((s) => (
                <button
                  key={s}
                  onClick={() => enviar(s)}
                  disabled={loading}
                  className="text-xs px-3 py-1.5 rounded-full border border-primary/40 hover:bg-primary/10 hover:border-primary text-foreground transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[80%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-4 py-2 text-sm"
                  : "max-w-[85%] rounded-2xl rounded-bl-sm bg-muted text-foreground px-4 py-2 text-sm leading-relaxed"
              }
            >
              {m.role === "user" ? m.content : renderContent(m.content)}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-muted text-muted-foreground px-4 py-2 text-sm inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Quebrando sua objeção…
            </div>
          </div>
        )}

        {erro && <p className="text-xs text-destructive">{erro}</p>}
      </div>

      {limiteAtingido ? (
        <div className="border-t border-border/60 px-3 py-3 bg-background/60">
          <Button
            onClick={() => navigate("/auth?mode=signup")}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-glow"
          >
            Agora é sua vez — eu faço as objeções e você me convence <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enviar(input);
          }}
          className="flex items-center gap-2 border-t border-border/60 px-3 py-3 bg-background/60"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={500}
            placeholder="Digite sua objeção…"
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground px-2"
            disabled={loading}
          />
          <Button type="submit" size="sm" disabled={loading || !input.trim()} className="bg-primary hover:bg-primary/90">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      )}

      {messages.some((m) => m.role === "assistant") && !limiteAtingido && (
        <div className="border-t border-border/60 px-5 py-4 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-foreground">Convencido? Teste com sua equipe agora.</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => navigate("/auth?mode=signup")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
            >
              Criar Conta Grátis <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/auth?mode=login")} className="border-primary/50">
              Já tenho conta
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default HeroObjecaoChat;
